import { ComponentRef, Injectable } from "@angular/core";
import { GenLaunchDialogService } from "./launch-dialog.service";
import { LAUNCH_CALLER } from "@spartacus/storefront";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { GlobalMessageService, GlobalMessageType } from "@spartacus/core";
import { Order, OrderFacade } from "@spartacus/order/root";
import { BehaviorSubject, EMPTY, of, throwError } from "rxjs";
import { Router } from "@angular/router";
import { GenPaymentTypeEnum } from "../enums/payment-type.enum";
import { ActiveCartFacade, Cart } from "@spartacus/cart/base/root";
import {
  findLimitedAvailabilityCheckoutError,
  LIMITED_AVAILABILITY_CHECKOUT_CONTINUE,
  LIMITED_AVAILABILITY_CHECKOUT_REVIEW_CART,
} from "../utils/limited-availability.utils";
import { GenLimitedAvailabilityCheckoutError } from "../models/limited-availability.model";
import { GenOrderDocumentErrorService } from './order-document-error.service';

@Injectable()
export class GenOrderService {
  isPlaceOrderBtnAvailable = new BehaviorSubject(true);

  constructor(
    protected orderFacade: OrderFacade,
    protected launchDialogService: GenLaunchDialogService,
    private globalMessageService: GlobalMessageService,
    private router: Router,
    private activeCartFacade: ActiveCartFacade,
    private documentErrorService: GenOrderDocumentErrorService,
  ) { }

  placeOrder(isFormValid: boolean, isDelego: boolean = false, showSpinner: boolean = false): void {
    if (showSpinner) {
      this.openPlaceOrderSpinner();
    }

    this.setIsPlaceOrderBtnAvailable(false);
    this.orderFacade.placeOrder(isFormValid).pipe(
      catchError((error) => {
        this.setIsPlaceOrderBtnAvailable(true);
        if (isDelego) this.closeDelegoModal();
        this.launchDialogService.closeDialog('close', LAUNCH_CALLER.PLACE_ORDER_SPINNER);

        const limitedAvailabilityError = findLimitedAvailabilityCheckoutError(error);

        if (limitedAvailabilityError) {
          this.handleLimitedAvailabilityCheckoutError(limitedAvailabilityError, isFormValid, isDelego);
          return EMPTY;
        }

        if (this.isOrderDocumentsRequiredError(error)) {
          this.documentErrorService.showRequiredError();
          return EMPTY;
        }

        if (error && error.status === 400) {
          this.globalMessageService.add(
            {
              key: 'checkoutReview.errorPlacingOrder',
            },
            GlobalMessageType.MSG_TYPE_ERROR
          );
          return throwError(() => error);
        }
        return of({} as Order);
      }),
    ).subscribe((order: Order) => {
      const isSplitWfOrder = order?.subCartCode && order?.paymentType?.code === GenPaymentTypeEnum.WELLS_FARGO;
      this.confirmOrderWithPlaceOrder(isDelego, isSplitWfOrder);
    })
  }

  setIsPlaceOrderBtnAvailable(val: boolean) {
    this.isPlaceOrderBtnAvailable.next(val);
  }

  private confirmOrderWithPlaceOrder(isDelego: boolean = false, isSplitWfOrder: boolean = false) {
    this.navigateToOrderConfirmation(undefined, isDelego, isSplitWfOrder);
  }

  private navigateToOrderConfirmation(component?: ComponentRef<any> | undefined, isDelego: boolean = false, isSplitWfOrder: boolean = false) {
    this.router.navigateByUrl(`/order-confirmation${isSplitWfOrder ? '?isSplitWfOrder=true' : ''}`).then(() => {
      this.globalMessageService.add(
        {
          key: 'checkoutReview.successPlacingOrder',
        },
        GlobalMessageType.MSG_TYPE_CONFIRMATION
      );
      if (isDelego) this.closeDelegoModal();
      this.launchDialogService.closeDialog('close', LAUNCH_CALLER.PLACE_ORDER_SPINNER);
      if (component) {
        component.destroy();
      }
    })
  }

  private closeDelegoModal() {
    this.launchDialogService.closeDialog('destroy', 'PAY_BY_DELEGO');
  }

  private isOrderDocumentsRequiredError(error: any): boolean {
    return error?.status === 400 && error?.error?.errors?.some(
      (validationError: { relatedField?: string }) => validationError.relatedField === 'orderDocuments'
    );
  }

  private handleLimitedAvailabilityCheckoutError(
    error: GenLimitedAvailabilityCheckoutError,
    isFormValid: boolean,
    isDelego: boolean
  ): void {
    this.activeCartFacade.reloadActiveCart();
    this.activeCartFacade.isStable()
      .pipe(
        filter(Boolean),
        switchMap(() => this.activeCartFacade.getActive()),
        filter(Boolean),
        take(1)
      )
      .subscribe((cart: Cart) => {
        this.openWarningDialog(
          {
            limitedAvailability: error,
            cartEmptied: !cart?.totalItems,
          },
          isFormValid,
          isDelego
        );
      });
  }

  private openWarningDialog(
    data: any,
    isFormValid: boolean,
    isDelego: boolean
  ): void {
    const dialog = this.launchDialogService.openDialog(
      'WARNING_DIALOG',
      undefined,
      undefined,
      data
    );

    if (!dialog) {
      return;
    }

    this.launchDialogService.dialogClose
      .pipe(take(1))
      .subscribe((reason) => {
        if (reason === LIMITED_AVAILABILITY_CHECKOUT_CONTINUE && !data.cartEmptied) {
          this.placeOrder(isFormValid, isDelego, true);
          return;
        }

        if (reason === LIMITED_AVAILABILITY_CHECKOUT_REVIEW_CART || data.cartEmptied) {
          this.router.navigate([data.redirectTo || '/cart']);
        }
      });

    dialog.pipe(take(1)).subscribe();
  }

  private openPlaceOrderSpinner(): void {
    this.launchDialogService.openDialog(LAUNCH_CALLER.PLACE_ORDER_SPINNER)
      ?.pipe(take(1))
      .subscribe();
  }
}
